import React, { createContext, useContext, useState } from "react";

const BookingContext = createContext();

export const useBooking = () => useContext(BookingContext);

// Package data - exported for use in components
export const PACKAGES = {
  quick_maintenance: {
    name: "Xpress Clean",
    tagline: "Quick refresh and basic maintenance",
    basePrices: { small: 89, medium: 109, large: 129, xl: 149 },
    interior: [
      "Quick carpet spray",
      "Vacuum seats, carpet, boot, floor mats",
      "Dash, console, cup holders cleaned",
      "Shampoo & wipe down all interior surfaces",
      "Windows & mirrors cleaned",
      "Protective spray wax sealant",
      "Light smell neutraliser"
    ],
    exterior: [
      "Pre-rinse vehicle",
      "Wheels, tyres & arches cleaned",
      "Snow foam with safe contact wash & dry",
      "Tyres shine glossed",
      "Protective gloss enhancer"
    ]
  },
  interior_xpress: {
    name: "Interior Xpress Detail",
    tagline: "Deep interior clean only",
    basePrices: { small: 125, medium: 145, large: 165, xl: 185 },
    interior: [
      "Pre-soaked carpet with anti odour cleaner",
      "Shampoo & conditioned wipe down all surfaces",
      "Leather seats cleaned (if applicable)",
      "Vacuum seats, seat belts, carpet, boot, mats",
      "Dash, steering wheel, console, trims cleaned",
      "Foot pedals cleaned",
      "Air vents, seat rails & sun visors cleaned",
      "Interior window glass cleaned",
      "Interior smell neutralised"
    ],
    exterior: []
  },
  deal_xpress: {
    name: "Deal Xpress Detail",
    tagline: "Clean and maintenance",
    basePrices: { small: 149, medium: 169, large: 189, xl: 219 },
    popular: true,
    interior: [
      "Pre-soaked carpet with anti odour cleaner",
      "Shampoo & conditioned wipe down all surfaces",
      "Leather seats cleaned (if applicable)",
      "Vacuum seats, seat belts, carpet, boot, mats",
      "Dash, steering wheel, console, trims cleaned",
      "Foot pedals cleaned",
      "Air vents, seat rails & sun visors cleaned",
      "Quick window glass wipe down",
      "Interior smell neutralised"
    ],
    exterior: [
      "Pre-rinse & snow foamed vehicle",
      "High pressure rinse and pH neutral snow foam wash",
      "Protective paint spray and gloss enhancer",
      "Wheels, tyres & arches cleaned",
      "Tyres shine glossed",
      "Door & boot jambs pressure cleaned & dried",
      "Windows & mirror cleaned (inside & out)"
    ]
  },
  full_xpress: {
    name: "Full Xpress Detail",
    tagline: "Complete deep cleanse and restoration",
    basePrices: { small: 245, medium: 265, large: 285, xl: 325 },
    includes: "up to 1yr Ceramic & paint spray coating!",
    interior: [
      "Complete soaked carpet with anti odour cleaner",
      "Complete air out all dust on tight spots",
      "Mud / dust / grime removal",
      "Deep shampoo & steam clean seats, carpet, boot & mats",
      "Leather seats shampooed & conditioned",
      "Vacuum seats, seat belts, carpet, boot, mats",
      "Dash, steering wheel, console, trims cleaned",
      "All plastics restored & protected",
      "Air vents deep cleaned",
      "Foot pedals cleaned with rubber protection",
      "Quick ceramic window spray",
      "All windows cleaned inside & out",
      "Premium smell neutraliser"
    ],
    exterior: [
      "Pre-rinse & pH snow foamed vehicle",
      "High pressure rinse and pH neutral snow foam wash of entire vehicle",
      "2x Snow foam contact wash",
      "Fuel door pressure cleaned & dried",
      "Protective paint spray and gloss enhancer",
      "Bug splash & road tar removed (when needed)",
      "Rubbers & plastics glossed",
      "Windows & mirrors cleaned",
      "Tyres, wheel arches & mud flaps glossed",
      "Brake dust aired out"
    ]
  }
};

export const ADDONS = [
  { id: "ceramic_protection", name: "Quick Spray Ceramic Protection", price: 70 },
  { id: "quick_gloss", name: "Quick Gloss", price: 30 },
  { id: "smell_neutraliser", name: "Smell Neutraliser", price: 30 },
  { id: "ac_cleaning", name: "A/C Cleaning and Conditioning", price: 60 },
  { id: "headlight_restoration", name: "Headlight Restoration", price: 60 },
  { id: "engine_detail", name: "Engine Bay Detail", price: 70 },
  { id: "pet_hair_removal", name: "Pet Hair Removal", price: 40 },
  { id: "plastic_trim_restoration", name: "Plastic Trim Restoration", price: 60 },
  { id: "leather_conditioning", name: "Leather Seat Conditioning", price: 50 }
];

export const VEHICLE_TYPES = [
  { id: "small", name: "Small", icon: "Car", desc: "Hatch, Sedan, Small SUV" },
  { id: "medium", name: "SUV", icon: "Truck", desc: "Medium SUV, Wagon" },
  { id: "large", name: "Large", icon: "Truck", desc: "Large SUV, Ute" },
  { id: "xl", name: "XL", icon: "Truck", desc: "Van, 7 Seater, 4WD" }
];

export const BookingProvider = ({ children }) => {
  const [vehicleType, setVehicleType] = useState("small");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [addons, setAddons] = useState([]);
  const [discountCode, setDiscountCode] = useState("");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showDiscountPopup, setShowDiscountPopup] = useState(false);
  const [popupType, setPopupType] = useState("normal_10");

  return (
    <BookingContext.Provider value={{
      vehicleType, setVehicleType,
      selectedPackage, setSelectedPackage,
      addons, setAddons,
      discountCode, setDiscountCode,
      showBookingModal, setShowBookingModal,
      showDiscountPopup, setShowDiscountPopup,
      popupType, setPopupType,
      PACKAGES,
      ADDONS
    }}>
      {children}
    </BookingContext.Provider>
  );
};

export default BookingContext;
